import { App, Button, Flex, Popconfirm, Space, Typography } from 'antd'
import { Notification } from '../../models/Notification'
import {
  DeleteOutlined,
  EditOutlined,
  EyeOutlined,
  SendOutlined,
} from '@ant-design/icons'
import { sendNotification as sendNotificationAPI } from '../../services/NotificationService'
import Audience from '../audience/Audience'
import { SBModelComponent } from '@supplement-bacon/react-toolkit/models'
import { useAppSelector } from '../../store/hooks'
import { selectNotificationById } from '../../store/NotificationReducer'
import { useState } from 'react'
import { DEFAULT_ERROR, DEFAULT_SUCCESS } from '../../App'
import HasAccess from '../HasAccess'
import { Permission } from '../../utils/permissions'

const { Text } = Typography

type Props = SBModelComponent<Notification> & {
  viewNotif: (model: Notification) => void
  updateNotif: (model: Notification) => void
  deleteNotif: (model: Notification) => void
  onNotificationSent: () => void
}

const NotificationActionsColumn = ({
  id,
  model,
  viewNotif,
  updateNotif,
  deleteNotif,
  onNotificationSent,
}: Props) => {
  const { message } = App.useApp()
  const item = model ? model : useAppSelector(selectNotificationById(id!))

  const [isSending, setIsSending] = useState<boolean>(false)

  if (!item) return <></>

  if (item.sentAt && item.sentAt !== undefined) {
    return (
      <Space>
        <Button icon={<EyeOutlined />} onClick={() => viewNotif(item)}>
          View details
        </Button>
      </Space>
    )
  }

  const sendNotification = () => {
    setIsSending(true)
    sendNotificationAPI(item)
      .then(() => {
        message.success(DEFAULT_SUCCESS)
        onNotificationSent()
      })
      .catch(() => {
        message.error(DEFAULT_ERROR)
      })
      .finally(() => {
        setIsSending(false)
      })
  }

  return (
    <Space>
      <HasAccess permissions={[Permission.sendPush]}>
        <Popconfirm
          title={
            <Text>
              You will send your notification to{' '}
              <Audience.UsersCount
                audienceId={item.audience}
                showOnlySubscribedCount
              />{' '}
              user(s).
            </Text>
          }
          okButtonProps={{ size: 'middle', icon: <SendOutlined /> }}
          okText={'Send now'}
          onConfirm={sendNotification}
          showCancel={false}
        >
          <Button
            loading={isSending}
            disabled={isSending}
            type="primary"
            icon={<SendOutlined />}
          >
            Send
          </Button>
        </Popconfirm>
      </HasAccess>
      <HasAccess permissions={[Permission.updatePush]}>
        <Button
          icon={<EditOutlined />}
          onClick={() => {
            updateNotif(item)
          }}
        />
      </HasAccess>
      <HasAccess permissions={[Permission.deletePush]}>
        <Button
          danger
          icon={<DeleteOutlined />}
          onClick={() => deleteNotif(item)}
        />
      </HasAccess>
    </Space>
  )
}

type NotificationActionsColumnType = {
  ActionsColumn: typeof NotificationActionsColumn
}

export { NotificationActionsColumn, type NotificationActionsColumnType }
